<template>
  <div class="hero">
    <div class="landing variant-bg">
      <div class="guide-wrapper" :style="wrapperStyle">
        <div class="guide variant-guide">
          <h1>
            <b class="desktop-text" v-html="contents.headline"></b>
            <b class="mobile-text" v-html="contents.headlineMobile || contents.headline"></b>
          </h1>
          <p v-if="isDesktop" v-html="contents.subText" class="landing-text"></p>
          <p v-else v-html="contents.subTextMobile || contents.subText" class="landing-text"></p>
          <router-link v-if="userIsAuthenticated" to="/company/modads">
            <button class="signup" :style="contents.buttonStyle">
              {{ contents.buttonText }}
            </button>
          </router-link>
          <button v-else class="signup" :style="contents.buttonStyle" @click="clickLeadCtaBtn">
            {{ contents.buttonText }}
          </button>
          <v-row justify="center" align="center" style="max-height: 0px">
            <ConsultDialog :show.sync="showConsultDialog" />
            <LeadModal :showModal.sync="showLeadModal" :source-route="modalSourceRoute" />
          </v-row>
        </div>
        <img v-if="isDesktop" :src="desktopImage" :style="variantImageStyle" alt="블로그 체험단 서비스" />
      </div>

      <!-- 모바일 이미지 -->
      <img v-if="!isDesktop" :src="mobileImage" :style="mobileImageStyle" alt="블로그 체험단" />
    </div>
    <Fab v-if="!showLeadModal" :showLeadModal.sync="showLeadModal" />
  </div>
</template>

<script>
import userMixin from '@/components/shared/userMixin';
import ConsultDialog from '@/components/company/ConsultDialog';
import LeadModal from '@/components/lead/modal/LeadModal.vue';
import Fab from './Fab.vue';
import { pushDataLayer } from '@/utils';

const LANDING_CONTENTS = {
  badge: null,
  badgeStyle: 'background-color: #0066ff',
  headline: '<span class="highlight">상위 1% 사장님</span>만 아는 매출 비밀,<br />지금 확인하세요.',
  headlineMobile: '<span class="highlight">상위 1% 사장님</span>만 아는<br />매출 비밀,<br />지금 확인하세요.',
  subText: '2만 명 사장님이 검증한 블로그 체험단의 성과',
  buttonText: '무료 상담하기',
  buttonStyle: 'background-color: #ff3b4b; padding: 16px; font-weight: bold;',
  showIcon: false,
  image: 'landing-logo-variant-B.png',
  imageMobile: 'landing-logo-mobile-variant-B.png',
};

export default {
  name: 'Landing',
  mixins: [userMixin],
  components: { ConsultDialog, LeadModal, Fab },

  mounted() {
    // NOTE: query parameter 체크는 views/landing/Landing.vue에서 처리함
    this.initializeTracking();
  },

  data() {
    return {
      showConsultDialog: false,
      showLeadModal: false,
      modalSourceRoute: 'storeLanding', // 기본값
    };
  },

  computed: {
    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp;
    },

    contents() {
      return LANDING_CONTENTS;
    },

    desktopImage() {
      try {
        return require(`@/assets/img/landing/ceo-landing/${this.contents.image}`);
      } catch {
        return require('@/assets/img/landing/ceo-landing/landing-logo.png');
      }
    },

    mobileImage() {
      try {
        return require(`@/assets/img/landing/ceo-landing/${this.contents.imageMobile}`);
      } catch {
        return require('@/assets/img/landing/ceo-landing/landing-logo-mobile.png');
      }
    },

    wrapperStyle() {
      if (this.isDesktop) {
        return {
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          position: 'relative',
          padding: '0 60px',
        };
      }
      return {
        maxWidth: '1440px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      };
    },

    aImageStyle() {
      return {
        width: '596px',
        marginLeft: '70px',
      };
    },

    variantImageStyle() {
      return {
        position: 'absolute',
        width: '819px',
        height: '595px',
        right: '-20px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
      };
    },

    mobileImageStyle() {
      return {
        width: this.$vuetify.breakpoint.smAndUp ? '400px' : '100%',
        maxWidth: '400px',
        objectFit: 'contain',
      };
    },
  },

  methods: {
    initializeTracking() {
      // Pipedrive 스크립트
      const pipedriveScript = document.createElement('script');
      pipedriveScript.setAttribute('src', 'https://webforms.pipedrive.com/f/loader');
      document.head.appendChild(pipedriveScript);

      // 랜딩 페이지 추적
      pushDataLayer({
        event: 'onboardLanding',
        landingFrom: 'lead',
        page: 'storeLanding',
      });
    },

    checkQueryParameters() {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldOpenModal = urlParams.get('openLeadModal') === 'true' || urlParams.get('inquiry') === 'true';
      const utmCampaign = urlParams.get('utm_campaign');

      if (shouldOpenModal) {
        console.log('🟢 [Landing] Opening modal via query parameter');

        // GTM 중복 클릭 차단 플래그 먼저 설정 (모달 열기 전!)
        sessionStorage.setItem('leadModalOpenedByQuery', 'true');
        console.log('🔒 [Landing] sessionStorage flag set');

        // utm_campaign에 따라 sourceRoute 설정
        if (utmCampaign === 'serviceGuide') {
          this.modalSourceRoute = 'serviceGuide';
        } else {
          this.modalSourceRoute = 'storeLanding';
        }

        // 쿼리 파라미터 제거
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        // logout 완료 후 모달 열고 플래그 계속 유지
        const openModal = () => {
          console.log('🚀 [Landing] Setting showLeadModal = true');
          this.showLeadModal = true;
          console.log('🔍 [Landing] showLeadModal is now:', this.showLeadModal);

          // 모달이 열린 후 5초간 플래그 유지
          setTimeout(() => {
            sessionStorage.removeItem('leadModalOpenedByQuery');
            console.log('🧹 [Landing] sessionStorage flag cleared');
          }, 5000);
        };

        // 다음 틱에 모달 열기 (최소 딜레이)
        this.$nextTick(openModal);
      }
    },

    clickLeadCtaBtn() {
      this.showLeadModal = true;

      // GTM 이벤트
      window.dataLayer.push({
        event: 'clickLeadCta',
        page: 'storeLanding',
        objectType: 'button',
        objectSection: 'main',
        objectId: 'openLeadForm',
        objectName: this.contents.buttonText,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.hero {
  background-color: #fff;
}

.landing {
  display: flex;
  overflow: hidden;
  margin: 0 auto;
  padding-top: 270px;
  justify-content: center;
  padding-bottom: 300px;
  position: relative;

  &.variant-bg {
    background: #1f2130;
    height: 620px;
    padding: 0;
    align-items: center;

    @media (max-width: 724px) {
      height: auto;
      padding: 80px 20px;
    }
  }

  * {
    user-select: none;
    -moz-user-select: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
}

.guide {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 16px;
  position: relative;

  h1 {
    line-height: 1.2;
    margin-bottom: 32px;
    font-weight: 300 !important;
    color: #0f1633;

    b {
      font-weight: 700;
    }
  }

  p {
    color: #1b2b43;
    margin-bottom: 56px;
    font-size: 15px;
    line-height: 1.79;
    letter-spacing: 0.01em;

    &.landing-text {
      color: #fff;
      font-size: 18px;
      font-weight: 500;
      line-height: 28px;
    }
  }

  &.variant-guide {
    h1 {
      margin-bottom: 16px;
    }

    p {
      margin-bottom: 48px;
    }
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 112px;
  height: auto;
  min-height: 24px;
  color: white;
  margin-bottom: 16px;
  line-height: 1.2;
  border-radius: 30px;
  padding: 4px 12px;
  white-space: nowrap;
}

.mobile-text {
  display: none;
  color: #fff;
  font-size: 28px;
  font-weight: 700;
}

::v-deep .highlight {
  color: #ff3b4b;
  font-weight: 700;
}

.desktop-text {
  display: inline;
  color: #fff;
  font-size: 48px;
  line-height: normal;
  font-weight: 700;
}

.signup {
  width: 220px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #ffffff;
  line-height: 1;
}

.newPrimaryBg {
  background-color: #ea2a3a;
}

@media (max-width: 724px) {
  .landing {
    height: auto;
    min-height: 620px;
    padding: 50px 0 0 0;
    flex-direction: column;

    .guide-wrapper {
      flex-direction: column !important;
      text-align: center;

      .guide {
        align-items: center !important;
        text-align: center !important;
        margin-bottom: 20px;
      }
    }
  }

  .guide {
    text-align: center;
    align-items: center;

    h1 {
      font-size: 28px;
      margin-bottom: 30px;
      letter-spacing: 0.02em !important;
    }

    &.variant-guide {
      h1 {
        margin-bottom: 16px;
      }

      p {
        margin-bottom: 24px;
      }
    }

    p {
      margin-bottom: 50px;
      font-size: 14px;

      &.landing-text {
        color: #fff;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
      }
    }
  }

  .mobile-text {
    display: inline !important;
  }

  .desktop-text {
    display: none !important;
  }
}
</style>
